import React, { useState, useContext } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  CircularProgress,
  Box,
  Checkbox,
  FormControlLabel,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  qrContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: theme.spacing(2),
  },
  secretText: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1),
    backgroundColor: "#f5f5f5",
    borderRadius: 4,
    fontFamily: "monospace",
    fontSize: 14,
    wordBreak: "break-all",
    textAlign: "center",
  },
  recoveryCodesContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    justifyContent: "center",
    padding: theme.spacing(2),
  },
  recoveryCode: {
    fontFamily: "monospace",
    fontSize: 14,
    padding: "4px 8px",
    backgroundColor: "#f5f5f5",
    borderRadius: 4,
    border: "1px solid #ddd",
  },
}));

const TwoFactorSetup = ({ userId, isAdmin, isOwnProfile, twoFactorEnabled, onUpdate }) => {
  const classes = useStyles();
  const { user: loggedInUser } = useContext(AuthContext);

  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("status"); // status, qr, recovery
  const [loading, setLoading] = useState(false);
  const [otpauthUri, setOtpauthUri] = useState("");
  const [secret, setSecret] = useState("");
  const [verifyToken, setVerifyToken] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [confirmed, setConfirmed] = useState(false);
  const [disablePassword, setDisablePassword] = useState("");

  const handleOpen = () => {
    setOpen(true);
    setStep("status");
    setVerifyToken("");
    setRecoveryCodes([]);
    setConfirmed(false);
    setDisablePassword("");
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSetup = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/2fa/setup");
      setOtpauthUri(data.otpauthUri);
      setSecret(data.secret);
      setStep("qr");
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  const handleVerify = async () => {
    if (!verifyToken || verifyToken.length < 6) {
      toast.error(i18n.t("twoFactor.setup.invalidCode"));
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/2fa/verify", { token: verifyToken });
      setRecoveryCodes(data.recoveryCodes);
      setStep("recovery");
      if (onUpdate) onUpdate(true);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  const handleDisable = async () => {
    setLoading(true);
    try {
      if (isOwnProfile) {
        await api.delete("/2fa/disable", { data: { password: disablePassword } });
      } else if (isAdmin) {
        await api.delete(`/2fa/admin-disable/${userId}`);
      }
      toast.success(i18n.t("twoFactor.setup.disabled"));
      if (onUpdate) onUpdate(false);
      handleClose();
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  const handleCopyCodes = () => {
    navigator.clipboard.writeText(recoveryCodes.join("\n"));
    toast.success(i18n.t("twoFactor.setup.codesCopied"));
  };

  return (
    <>
      <Button
        variant="outlined"
        color={twoFactorEnabled ? "secondary" : "primary"}
        onClick={handleOpen}
        size="small"
      >
        {twoFactorEnabled
          ? i18n.t("twoFactor.setup.manage")
          : i18n.t("twoFactor.setup.enable")}
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{i18n.t("twoFactor.setup.title")}</DialogTitle>
        <DialogContent>
          {step === "status" && (
            <Box textAlign="center" py={2}>
              <Typography variant="body1" gutterBottom>
                {twoFactorEnabled
                  ? i18n.t("twoFactor.setup.statusEnabled")
                  : i18n.t("twoFactor.setup.statusDisabled")}
              </Typography>
              {!twoFactorEnabled && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSetup}
                  disabled={loading}
                  style={{ marginTop: 16 }}
                >
                  {loading ? <CircularProgress size={24} /> : i18n.t("twoFactor.setup.activate")}
                </Button>
              )}
              {twoFactorEnabled && isOwnProfile && (
                <Box mt={2}>
                  <TextField
                    label={i18n.t("twoFactor.setup.passwordConfirm")}
                    type="password"
                    value={disablePassword}
                    onChange={(e) => setDisablePassword(e.target.value)}
                    variant="outlined"
                    margin="dense"
                    fullWidth
                  />
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleDisable}
                    disabled={loading || !disablePassword}
                    style={{ marginTop: 8 }}
                  >
                    {loading ? <CircularProgress size={24} /> : i18n.t("twoFactor.setup.deactivate")}
                  </Button>
                </Box>
              )}
              {twoFactorEnabled && isAdmin && !isOwnProfile && (
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleDisable}
                  disabled={loading}
                  style={{ marginTop: 16 }}
                >
                  {loading ? <CircularProgress size={24} /> : i18n.t("twoFactor.setup.adminDeactivate")}
                </Button>
              )}
            </Box>
          )}

          {step === "qr" && (
            <Box className={classes.qrContainer}>
              <Typography variant="body2" gutterBottom>
                {i18n.t("twoFactor.setup.scanQR")}
              </Typography>
              <Box my={2}>
                <QRCodeSVG value={otpauthUri} size={200} />
              </Box>
              <Typography variant="body2" gutterBottom>
                {i18n.t("twoFactor.setup.manualEntry")}
              </Typography>
              <Typography className={classes.secretText}>{secret}</Typography>
              <TextField
                label={i18n.t("twoFactor.setup.verifyCode")}
                value={verifyToken}
                onChange={(e) => setVerifyToken(e.target.value)}
                variant="outlined"
                margin="normal"
                fullWidth
                inputProps={{ maxLength: 6 }}
                placeholder="000000"
                autoFocus
              />
              <Button
                variant="contained"
                color="primary"
                onClick={handleVerify}
                disabled={loading || verifyToken.length < 6}
                fullWidth
                style={{ marginTop: 8 }}
              >
                {loading ? <CircularProgress size={24} /> : i18n.t("twoFactor.setup.verifyAndActivate")}
              </Button>
            </Box>
          )}

          {step === "recovery" && (
            <Box textAlign="center" py={2}>
              <Typography variant="body1" gutterBottom style={{ fontWeight: 600 }}>
                {i18n.t("twoFactor.setup.recoveryTitle")}
              </Typography>
              <Typography variant="body2" gutterBottom color="error">
                {i18n.t("twoFactor.setup.recoveryWarning")}
              </Typography>
              <Box className={classes.recoveryCodesContainer}>
                {recoveryCodes.map((code, idx) => (
                  <Typography key={idx} className={classes.recoveryCode}>
                    {code}
                  </Typography>
                ))}
              </Box>
              <Button
                variant="outlined"
                onClick={handleCopyCodes}
                style={{ marginTop: 8 }}
              >
                {i18n.t("twoFactor.setup.copyCodes")}
              </Button>
              <Box mt={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={confirmed}
                      onChange={(e) => setConfirmed(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={i18n.t("twoFactor.setup.confirmSaved")}
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {step === "recovery" ? (
            <Button
              onClick={handleClose}
              color="primary"
              variant="contained"
              disabled={!confirmed}
            >
              {i18n.t("twoFactor.setup.done")}
            </Button>
          ) : (
            <Button onClick={handleClose} color="secondary">
              {i18n.t("twoFactor.setup.close")}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TwoFactorSetup;
